import Link from 'next/link'
import { Card, CardBody } from 'reactstrap'
import BreadcrumbBar from '~/components/shared/BreadcrumbBar'

function OktaVerify () {
  const breadcrumbs = [
    { name: 'Login Help', href: '/login-help' },
    { name: 'Okta Verify', href: '/login-help/okta-verify' }
  ]

  return (
    <>
      <BreadcrumbBar breadcrumbs={breadcrumbs} />

      <h2 className='pb-2'>Okta Verify</h2>

      <Card>
        <CardBody>
          <p>
            Okta Verify is a mobile app that supplies a limited time code or push notification for logging into SCP.
            This app is available for iOS devices in the App Store and for Android devices in the Google Play Store.
          </p>
          <hr />
          <h2>
            Setting up Okta Verify
          </h2>
          <p>
            To begin, install Okta Verify by clicking on the links below.
            <br />
            <ul>
              <li>
                iOS: <a target='_blank' rel='noopener noreferrer' href='https://itunes.apple.com/us/app/okta-verify/id490179405'>https://itunes.apple.com/us/app/okta-verify/id490179405</a>
              </li>
              <li>
                Android: <a target='_blank' rel='noopener noreferrer' href='https://play.google.com/store/apps/details?id=com.okta.android.auth'>https://play.google.com/store/apps/details?id=com.okta.android.auth</a>
              </li>
            </ul>
            Log in to <Link href='/'>mySCPHealth.com</Link>.
            When prompted to set up multifactor authentication, select <b>Setup</b> under the Okta Verify option.
          </p>
          <p>
            If you have already logged in and setup an MFA method, you will need to <Link href='/login-help/change-auth-methods'><a>Change Authentication Methods</a></Link> and continue the setup.
          </p>
          <p>
            On the next screen, select your device type and click Next.
          </p>
          <img className='mb-4' src='/static/img/okta-setup.png' alt='okta setup' />
          <p>
            You should now see a QR Code on your screen, which is a square barcode.
            You will configure Okta Verify by scanning this code with your phone’s camera.
          </p>
          <img className='mb-4' src='/static/img/okta-scan.png' alt='okta scan' />
          <p>
            <b>If you are using a desktop computer</b>, open the Okta Verify app on your phone.
            Tap the + button to add a new account.
            You may need to grant the app access to your device camera in order to continue.
            The app will then open your camera.
            Use this to scan the QR Code, and Okta Verify will add this account to your app.
          </p>
          <p>
            Once the app is configured, the setup page should now show Okta Verify under <b> Enrolled Factors</b> with a green check mark.
            Click <b>Finish</b> to complete the process.
          </p>
          <p>
            <b>If you are using a mobile device</b>, tap <b>“Can’t scan?”</b> under the QR code.
            You will be given the option to activate Okta Verify via Email, SMS, or manually without Push Authentication.
            Follow the below instructions for your chosen activation method:
          </p>
          <p>
            <b>Email</b>
            <ul>
              <li>
                Select <b>Send activation link via Email</b> and tap <b>Next</b>.
              </li>
              <li>
                On your mobile device, check your inbox for an email from Okta.
                Open it and tap <b>Activate Okta Verify Push</b>.
              </li>
              <li>
                The Okta Verify app will open, and you should see a message that reads: <b>“Enrolled in Push Authentication”</b>.
              </li>
              <li>
                Return to the setup page and ensure Okta Verify is displayed under Enrolled Factors.
              </li>
              <li>
                Tap <b>Finish</b> to complete the process.
              </li>
            </ul>
          </p>
          <p>
            <b>SMS</b>
            <ul>
              <li>
                Select <b>Send activation link via SMS</b>.
              </li>
              <li>
                Enter your cell phone number and tap Next.
              </li>
              <li>
                You will receive a text message from Okta. Open this message and tap the link in it.
              </li>
              <li>
                The Okta Verify app will open, and you should see a message that reads <b>“Enrolled in Push Authentication”</b>.
              </li>
              <li>
                Return to the setup page and ensure Okta Verify is displayed under <b>Enrolled Factors</b>.
              </li>
              <li>
                Tap <b>Finish</b> to complete the process.
              </li>
            </ul>
          </p>
          <p>
            <b>Manual Setup without Push Authentication (not recommended)</b>
            <ul>
              <li>
                Select <b>Setup manually without Push Authentication</b>.
                This will display a secret key you will use to configure the app.
              </li>
              <li>
                Open the Okta Verify app.
                Tap the + button, then tap <b>“No Barcode?”</b>.
              </li>
              <li>
                Enter your Okta account username and the secret key displayed on the setup screen, then tap <b>Add Account</b>.
              </li>
              <li>
                On the setup page, tap <b>Next</b>.
                Tap the code displayed in the Okta Verify app to copy it, then paste it in the <b>Enter code</b> field and tap <b>Verify</b>.
              </li>
              <li>
                Ensure Okta Verify is now displayed under <b>Enrolled Factors</b>.
              </li>
              <li>
                Tap <b>Finish</b> to complete the process.
              </li>
            </ul>
          </p>
        </CardBody>
      </Card>
    </>
  )
}

OktaVerify.publicAccess = true

OktaVerify.getInitialProps = () => ({
  title: 'Okta Verify'
})

export default OktaVerify
